'use client';

import React, { useState } from 'react';
import { Lock, Eye, EyeOff, User, Phone, Mail, Home } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type PrivacySetting = {
  id: string;
  Icon: React.ElementType;
  label: string;
  desc: string;
  defaultVisible: boolean;
};

const privacySettings: PrivacySetting[] = [
  {
    id: 'priv-firstname',
    Icon: User,
    label: 'First name',
    desc: 'Shown to finder so they can address you personally',
    defaultVisible: true,
  },
  {
    id: 'priv-chat',
    Icon: Mail,
    label: 'RETURNI Secure Chat',
    desc: 'Anonymous messaging — always enabled for communication',
    defaultVisible: true,
  },
  {
    id: 'priv-phone',
    Icon: Phone,
    label: 'Phone number',
    desc: 'Your mobile number — hidden by default',
    defaultVisible: false,
  },
  {
    id: 'priv-email',
    Icon: Mail,
    label: 'Email address',
    desc: 'Your email address — hidden by default',
    defaultVisible: false,
  },
  {
    id: 'priv-address',
    Icon: Home,
    label: 'Home address',
    desc: 'Your home address — always hidden, never shown',
    defaultVisible: false,
  },
];

export default function PrivacyControls() {
  const [visibility, setVisibility] = useState<Record<string, boolean>>(
    Object.fromEntries(privacySettings.map((s) => [s.id, s.defaultVisible]))
  );

  const toggle = (id: string, locked: boolean) => {
    if (locked) return;
    setVisibility((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isLocked = (id: string) => id === 'priv-address' || id === 'priv-chat';

  return (
    <div className="bg-white rounded-2xl border border-border shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center gap-3">
        <div className="w-8 h-8 bg-primary/10 rounded-xl flex items-center justify-center">
          <Lock size={15} className="text-primary" />
        </div>
        <div>
          <h2 className="font-700 text-base text-foreground">Privacy Controls</h2>
          <p className="text-xs text-muted-foreground">Choose what the finder can see</p>
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-col gap-3">
          {privacySettings.map(({ id, Icon, label, desc }) => {
            const locked = isLocked(id);
            const isVisible = visibility[id];

            return (
              <div
                key={id}
                className={`flex items-center gap-4 p-3.5 rounded-xl border transition-all duration-150 ${
                  locked
                    ? 'border-border bg-secondary/30 opacity-70 cursor-not-allowed'
                    : isVisible
                    ? 'border-accent/30 bg-accent/5 cursor-pointer hover:bg-accent/8' :'border-border bg-white cursor-pointer hover:bg-secondary/50'
                }`}
                onClick={() => toggle(id, locked)}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isVisible ? 'bg-accent/15' : 'bg-secondary'}`}>
                  <Icon size={15} className={isVisible ? 'text-accent' : 'text-muted-foreground'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-600 text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{desc}</p>
                </div>
                <div className={`flex items-center gap-1.5 flex-shrink-0 ${locked ? 'text-muted-foreground' : isVisible ? 'text-accent' : 'text-muted-foreground'}`}>
                  {isVisible ? <Eye size={15} /> : <EyeOff size={15} />}
                  <span className="text-xs font-600">
                    {locked && id === 'priv-address' ? 'Always hidden' : locked ? 'Always on' : isVisible ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
          Your personal information is never automatically revealed when someone scans a RETURNI label. Only information you choose to share will be visible to a finder.
        </p>
      </div>
    </div>
  );
}